/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppContextProvider } from '@tock/money';
import { Button, TockDialog } from 'omakase';
import { ThemeProvider as EmotionThemeProvider, Theme, css } from '@emotion/react';
import { IntlProvider } from 'react-intl';
import { StylesProvider, ThemeProvider } from '@material-ui/core/styles';
import { createRef } from 'react';
import { getTheme } from 'theme/Theme';
import { jss } from 'utils/jssInsertionOrderUtils';
import { oc } from 'ts-optchain';
import BaseComponent from '@tock/shared/components/BaseComponent';
import connect, { DefaultConnectProps } from '@tock/shared/utils/connect';

interface Props {
  cancelText?: string;
  continueText?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
  modifiers?: string[];
  onCancel(): void;
  onContinue(): void;
  text: JSX.Element | string;
  title?: string;
}

class ConfirmAction extends BaseComponent<Props & DefaultConnectProps> {
  static defaultProps: Partial<Props> = {
    cancelText: 'No, cancel',
    continueText: 'Yes, continue',
    isDestructive: false,
    isLoading: false,
    modifiers: [],
    title: 'Are you sure?',
  };

  confirmButton = createRef<any>();

  componentDidMount() {
    if (this.confirmButton && this.confirmButton.current) {
      this.confirmButton.current.focus();
    }
  }

  handleCancel() {
    this.props.onCancel();
  }

  handleContinue() {
    this.props.onContinue();
  }

  render() {
    const {
      app,
      features,
      currency,
      locale,
      timeZone,
      cancelText,
      continueText,
      isLoading,
      text,
      title,
    } = this.props;

    const footer = (
      <div
        css={({ spacing }: Theme) => css`
          display: flex;
          width: 100%;

          > * + * {
            margin-left: ${spacing(1)}px;
          }
        `}
      >
        <Button
          size="large"
          disabled={isLoading}
          fullWidth
          variant="basic"
          onClick={this.handleCancel}
        >
          {cancelText}
        </Button>
        <Button
          ref={this.confirmButton}
          size="large"
          data-testid="ContinueButton"
          disabled={isLoading}
          fullWidth
          variant="primary"
          onClick={this.handleContinue}
        >
          {continueText}
        </Button>
      </div>
    );

    const theme = getTheme();

    return (
      <StylesProvider jss={jss}>
        <ThemeProvider theme={theme}>
          <EmotionThemeProvider theme={theme}>
            <IntlProvider locale={locale}>
              <AppContextProvider
                appContext={(oc(app) as any).appContext()}
                currency={currency}
                locale={locale}
                features={features}
                timeZone={timeZone}
              >
                <TockDialog
                  open={true}
                  onClose={this.handleCancel}
                  headingConfig={{
                    left: title
                      ? {
                          configType: 'title',
                          title: title,
                        }
                      : undefined,
                  }}
                  type="sheet"
                  actionsContent={footer}
                >
                  <div className="ConfirmationModal-body">
                    <div className="ConfirmAction">{text}</div>
                  </div>
                </TockDialog>
              </AppContextProvider>
            </IntlProvider>
          </EmotionThemeProvider>
        </ThemeProvider>
      </StylesProvider>
    );
  }
}

export default connect<Props>(ConfirmAction);
